/**
 * Historique des pesées — « consultable et **corrigeable** » (specs §6.6).
 *
 * 🔴 **Aucune immuabilité ici.** Le §4.1 fige les macros d'une entrée de journal ;
 * une pesée, elle, se corrige — c'est le mot des specs. Corriger et saisir sont
 * donc la même opération : un `PUT` sur la date. Ne pas transposer ici les
 * précautions du journal, elles n'ont pas le même objet.
 *
 * **Une liste, pas un calendrier.** Un calendrier mensuel afficherait surtout des
 * cases vides et demanderait deux gestes pour atteindre le mois dernier, alors que
 * la question posée à cet écran est « qu'est-ce que j'ai pesé ces jours-ci ». Une
 * liste antéchronologique répond du premier coup d'œil, se lit avec le même geste
 * que le reste de l'application, et affiche le lissé **à côté** du brut — ce qu'une
 * grille de dates ne peut pas faire tenir dans une case.
 *
 * ⚠️ La liste ne montre que la **plage courante**. C'est voulu : les quatre
 * boutons de plage pilotent l'écran entier, graphique et historique ensemble, ce
 * qui garantit que les deux racontent toujours la même chose. « Tout » donne
 * l'historique complet.
 *
 * ⚠️ **Refonte** : la liste est celle du prototype (`.hist-row` — date à gauche,
 * valeur et lissé à droite, filet entre les lignes), et la note « toucher une
 * ligne » descend en `.foot-note` sous la carte. La feuille de correction garde
 * son mécanisme (`Sheet`, portail, gestes) ; seuls son champ et ses deux boutons
 * passent aux composants de la refonte.
 */

import { useState } from 'react';

import type { WeightPointDTO } from '../../../../shared/types';
import { Button, Card, TextField } from '../../components/nourri';
import { Hint, Sheet } from '../../components/ui/primitives';
import { toNumber } from '../../lib/formFields';
import { formatDayRow, formatKg, MAX_KG, MIN_KG } from './weightFields';

function HistoryRow({ point, onEdit }: { point: WeightPointDTO; onEdit: () => void }) {
  return (
    <li>
      <button type="button" onClick={onEdit} className="n-hist-row">
        <span className="n-hist__d">{formatDayRow(point.date)}</span>
        {/*
          ⚠️ Deux nombres côte à côte sur une ligne étroite : ils sont dans une
          boîte à `nowrap` **et** en chiffres tabulaires, et le libellé de gauche
          se tronque avant eux. C'est le même défaut que celui corrigé sur les
          jauges de repas à l'étape 7 — deux valeurs qui s'allongent finissent
          par se chevaucher — traité ici par construction.
        */}
        <span className="n-hist__v tnum">
          {formatKg(point.kg)} kg <small>· lissé {formatKg(point.smoothed)}</small>
        </span>
      </button>
    </li>
  );
}

/**
 * Ce que la feuille est en train de faire : corriger une pesée existante, ou
 * en **ajouter une à une date passée**.
 *
 * 🔴 Le second cas manquait, et c'était un trou fonctionnel, pas une commodité.
 * La carte du haut ne sait saisir que **le jour courant**, et l'historique ne
 * liste que les jours **déjà pesés** : un jour oublié était donc définitivement
 * hors d'atteinte depuis l'application, alors que le serveur l'accepte depuis
 * l'étape 8 — `PUT /api/weight/:date` prend n'importe quelle date, et les specs
 * §6.6 demandent un historique « corrigeable ».
 */
type Editing = { mode: 'edit'; point: WeightPointDTO } | { mode: 'add' };

export function WeightHistory({
  points,
  today,
  saving,
  onSave,
  onDelete,
}: {
  points: readonly WeightPointDTO[];
  /** Jour courant **du serveur** — borne haute des dates saisissables. */
  today: string;
  saving: boolean;
  onSave: (date: string, value: number) => void;
  onDelete: (date: string) => void;
}) {
  const [editing, setEditing] = useState<Editing | null>(null);
  const [draft, setDraft] = useState('');
  const [draftDate, setDraftDate] = useState('');

  const openEdit = (point: WeightPointDTO) => {
    setDraft(formatKg(point.kg));
    setDraftDate(point.date);
    setEditing({ mode: 'edit', point });
  };

  const openAdd = () => {
    setDraft('');
    /*
     * ⚠️ Pré-remplie au jour courant, pas laissée vide : c'est le point de
     * départ le plus proche de ce qu'on cherche, et un champ de date vide sur
     * iOS demande trois gestes avant d'afficher quoi que ce soit.
     */
    setDraftDate(today);
    setEditing({ mode: 'add' });
  };

  // `toNumber` rend 0 sur un champ vide : les bornes du serveur (20–400 kg)
  // rejettent donc le champ vide sans qu'il faille un test de plus.
  const value = toNumber(draft);
  const valid = value >= MIN_KG && value <= MAX_KG && draftDate !== '';

  // Antéchronologique : la pesée qu'on vient de corriger est celle du haut.
  const rows = [...points].reverse();

  return (
    <>
      <Card>
        <div className="n-row n-row--between">
          <h2 className="n-title">Historique</h2>
          {/*
            ⚠️ **Ici et pas dans la carte de pesée du jour.** Celle-ci est le
            chemin quotidien, qui doit tenir en deux taps (specs §4) ; ajouter
            un sélecteur de date dessus l'aurait alourdi pour un geste
            occasionnel. La réparation d'un oubli appartient à l'historique.
          */}
          <Button variant="ghost" shape="rounded" onClick={openAdd}>
            Ajouter
          </Button>
        </div>

        {rows.length === 0 ? (
          <p className="n-hint" style={{ marginTop: 10 }}>
            Aucune pesée sur cette période.
          </p>
        ) : (
          <ul className="n-list" style={{ marginTop: 4 }}>
            {rows.map((point) => (
              <HistoryRow
                key={point.date}
                point={point}
                onEdit={() => {
                  openEdit(point);
                }}
              />
            ))}
          </ul>
        )}
      </Card>

      {/*
        ⚠️ **Sous la carte, pas dedans** — prototype `.foot-note`. Elle commente
        la liste, elle n'en fait pas partie : à l'intérieur, elle se lirait comme
        une dernière ligne d'historique.
      */}
      {rows.length > 0 && (
        <p className="n-foot-note">Toucher une ligne pour corriger ou retirer cette pesée.</p>
      )}

      {editing && (
        <Sheet
          open
          title={editing.mode === 'edit' ? formatDayRow(editing.point.date) : 'Ajouter une pesée'}
          onClose={() => {
            setEditing(null);
          }}
        >
          {/*
            ⚠️ **Le champ de date n'apparaît qu'à l'ajout.** Corriger une pesée,
            c'est corriger *sa valeur* : rendre sa date modifiable ouvrirait un
            second geste — déplacer une pesée — que personne n'a demandé et qui
            écraserait silencieusement la pesée de la date d'arrivée, la date
            étant clé primaire.

            ⚠️ `max` borné au jour du **serveur** : on ne se pèse pas demain.
            C'est un garde-fou de saisie, pas une règle métier — le serveur
            reste seul juge.
          */}
          {editing.mode === 'add' && (
            <TextField
              label="Jour"
              type="date"
              max={today}
              value={draftDate}
              onChange={(event) => {
                setDraftDate(event.target.value);
              }}
              selectOnFocus={false}
            />
          )}

          <TextField
            label="Poids"
            unit="kg"
            numeric
            inputMode="decimal"
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value.replace(',', '.'));
            }}
            placeholder=""
            autoComplete="off"
          />

          <Hint>
            {editing.mode === 'add'
              ? 'Une pesée ajoutée après coup compte comme les autres : la moyenne lissée et la tendance sont recalculées à la lecture, jamais stockées.'
              : 'Corriger une pesée passée recalcule aussitôt la moyenne lissée et la tendance : rien n’est figé ici, contrairement au journal.'}
          </Hint>

          <div className="n-row">
            <Button
              variant="primary"
              shape="rounded"
              disabled={!valid || saving}
              style={{ flex: 1 }}
              onClick={() => {
                onSave(editing.mode === 'edit' ? editing.point.date : draftDate, value);
                setEditing(null);
              }}
            >
              Enregistrer
            </Button>
            {/*
              ⚠️ Pas de « Retirer » à l'ajout : il n'y a rien à retirer, et un
              bouton destructeur désactivé sur une feuille de création est un
              piège de lecture.
            */}
            {editing.mode === 'edit' && (
              <Button
                variant="danger"
                disabled={saving}
                onClick={() => {
                  onDelete(editing.point.date);
                  setEditing(null);
                }}
              >
                Retirer
              </Button>
            )}
          </div>
        </Sheet>
      )}
    </>
  );
}
